// src/routing/OpenRoutes.jsx
import React, { useEffect, useMemo, useState } from "react";
import { Navigate, useLocation } from "react-router-dom";
import api, { getToken, setToken, isTokenValid, clearAuth } from "../utils/axiosConfig";


export const OpenRoutes = ({ children }) => {
  const location = useLocation();

  const [checking, setChecking] = useState(true);
  const [authed, setAuthed] = useState(false);

  // ✅ Where to go if already authenticated
  const returnTo = useMemo(() => {
    return (
      location?.state?.returnTo ||
      location?.state?.from?.pathname ||
      "/"
    );
  }, [location?.state]);

  // ✅ If true, we allow the open page (no refresh attempt)
  const skipRefresh = useMemo(() => {
    return Boolean(location?.state?.skipRefresh);
  }, [location?.state]);

  useEffect(() => {
    let alive = true;

    const run = async () => {
      try {
        // ✅ getToken() should return ONLY a valid token or null
        const localToken = getToken();

        // 1) Already authenticated -> redirect
        if (localToken && isTokenValid(localToken)) {
          if (!alive) return;
          setAuthed(true);
          setChecking(false);
          return;
        }

        // If any stale token slipped in, clear it (safety)
        if (localToken && !isTokenValid(localToken)) {
          try {
            clearAuth?.();
          } catch {}
        }

        // 2) Explicitly skip refresh -> allow page
        if (skipRefresh) {
          if (!alive) return;
          setAuthed(false);
          setChecking(false);
          return;
        }

        // 3) Try refresh cookie
        const res = await api.post("user/refresh", {}, { withCredentials: true });
        const newToken = res.data?.token || res.data?.accessToken;

        if (!alive) return;

        if (newToken && isTokenValid(newToken)) {
          setToken(newToken);
          setAuthed(true);
        } else {
          // invalid refresh response
          try {
            clearAuth?.();
          } catch {}
          setAuthed(false);
        }
      } catch {
        if (!alive) return;
        setAuthed(false);
      } finally {
        if (!alive) return;
        setChecking(false);
      }
    };

    run();

    return () => {
      alive = false;
    };
  }, [skipRefresh]);

  if (checking) return null;

  return authed ? <Navigate to={returnTo} replace /> : children;
};